"use client";

import { PostHogErrorBoundary } from "@/components/ui/posthog-error-boundary";
import { Toaster } from "@/components/ui/sonner";
import { cognitoConfig } from "@/config";
import { createApolloClient } from "@/lib/apollo/client";
import { Auth } from "@/lib/auth";
import { AuthProvider } from "@/providers/auth-provider";
import { type Branding, BrandingProvider } from "@/providers/branding-provider";
import { ApolloProvider } from "@apollo/client";

const { userPoolId, clientId, region } = cognitoConfig;

// Create auth client instance outside component to prevent recreation
let authClientInstance: ReturnType<typeof Auth> | null = null;
let apolloClientInstance: ReturnType<typeof createApolloClient> | null = null;

const getAuthClient = () => {
  if (!authClientInstance) {
    authClientInstance = Auth({
      region,
      userPoolId,
      userPoolWebClientId: clientId,
      mandatorySignIn: true,
      authenticationFlowType: "CUSTOM_AUTH",
    });
  }
  return authClientInstance;
};

const getApolloClient = () => {
  if (!apolloClientInstance) {
    const authClient = getAuthClient();
    apolloClientInstance = createApolloClient(authClient);
  }
  return apolloClientInstance;
};

export function ClientProviders({
  children,
  branding,
}: {
  children: React.ReactNode;
  branding?: Branding;
}) {
  const authClient = getAuthClient();
  const apolloClient = getApolloClient();

  return (
    <PostHogErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <AuthProvider authClient={authClient}>
          <BrandingProvider branding={branding}>
            {children}
            <Toaster position="top-center" />
          </BrandingProvider>
        </AuthProvider>
      </ApolloProvider>
    </PostHogErrorBoundary>
  );
}
